/*
 * @Description: WhereUse组件 - 展示镜片使用场景
 * 从Vue组件转换而来，支持响应式设计，移动端使用轮播，PC端使用网格布局
 */
'use client';

import Image from '@/components/Image';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { useMediaQuery } from '@mui/material';
import React from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

// 定义数据项的类型
interface WhereUseItem {
  image: string;
  title: string;
  description: string;
}

// 定义组件props的类型
interface WhereUseData {
  data_array: WhereUseItem[];
  aspect_ratio?: string;
}

interface WhereUseProps {
  whereUseData: WhereUseData;
}

const WhereUse: React.FC<WhereUseProps> = ({ whereUseData }) => {
  // 获取设备类型
  const isMobileScreen = useMediaQuery('(max-width: 768px)');

  // 如果没有数据，不渲染组件
  if (!whereUseData?.data_array?.length) {
    return null;
  }

  // 移动端轮播组件
  const renderMobileSwiper = () => (
    <div className="swiper-nav-container relative">
      <Swiper
        modules={[Navigation]}
        slidesPerView="auto"
        spaceBetween={8}
        slidesOffsetBefore={14}
        slidesOffsetAfter={14}
        observer
        observeParents
        observeSlideChildren
        navigation={{
          nextEl: '.where-use-next',
          prevEl: '.where-use-prev',
        }}
        className="w-full"
      >
        {whereUseData.data_array.map((item, index) => (
          <SwiperSlide key={index} className="!w-[200px]">
            <div className=" flex flex-col items-center">
              <Image
                src={item.image}
                alt={item.title}
                width={180}
                height={180}
                className="w-[180px] h-auto block  box-border rounded-full"
                style={{
                  aspectRatio: whereUseData.aspect_ratio || '1/1',
                }}
              />
              <h3 className="text-[13px] mt-12 font-bb mb-[12px] text-center">{item.title}</h3>
              <div
                className="text-[12px] leading-[1.5] [&_ul]:ml-[16px] [&_ul_li]:list-disc px-[8px]"
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* 导航按钮 */}
      <SwiperNavigationButtons
        navigationPrefix="where-use"
        prevButtonPosition="left-2"
        nextButtonPosition="right-2"
      />
    </div>
  );

  // PC端网格布局组件
  const renderDesktopGrid = () => (
    <ul className="flex justify-between items-start flex-nowrap gap-[24px]">
      {whereUseData.data_array.map((item, index) => (
        <li key={index} className="w-[400px]">
          <div className="where-use-item-box flex flex-col items-center">
            <Image
              src={item.image}
              alt={item.title}
              width={400}
              height={400}
              className="w-full h-auto block px-[20px] pb-[32px] box-border rounded-full"
              style={{
                aspectRatio: whereUseData.aspect_ratio || '1/1',
              }}
            />
            <h3 className="text-[24px] font-bb mb-[12px] text-center">{item.title}</h3>
            <div
              className="text-[16px] leading-[1.5] [&_ul]:ml-[24px] [&_ul_li]:list-disc"
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="lenses-where-use w-full box-border pb-[40px] md:px-[240px] md:my-[104px]">
      {/* 移动端显示轮播，PC端显示网格 */}
      {isMobileScreen ? renderMobileSwiper() : renderDesktopGrid()}
    </div>
  );
};

export default WhereUse;
